import {
  ClipboardEdit,
  RefreshCw,
  ShieldCheck,
  Sprout,
  Truck,
  Warehouse,
} from 'lucide-react';

type Destination = { label: string; href: string };
export type FeatureDefinition = {
  id: string;
  name: string;
  category: string;
  headline: string;
  description: string;
  activeHeadline: string;
  activeDescription: string;
  icon: typeof ShieldCheck;
  setup: Destination;
  launch: Destination;
  secondary?: Destination;
  settings?: string;
  benefits: { title: string; description: string }[];
  steps: { title: string; description: string }[];
};

export const featureCatalog: FeatureDefinition[] = [
  {
    id: 'order-editing',
    name: 'Order Editing',
    category: 'AFTER CHECKOUT, BEFORE FULFILLMENT',
    icon: ClipboardEdit,
    headline: 'A change of plans.\nNot a support ticket.',
    description:
      'Give customers a window to update their order after checkout. Keep your team focused on getting it out the door.',
    activeHeadline: 'Your order editing workspace, ready when you are.',
    activeDescription:
      'Review customer change requests, track editing activity, and keep your editing rules aligned with fulfillment.',
    setup: { label: 'Set up Order Editing', href: '/order-settings/all' },
    launch: {
      label: 'Open Order Editing requests',
      href: '/requests?type=ORDER_EDITING',
    },
    secondary: {
      label: 'Open Order Editing requests',
      href: '/requests?type=ORDER_EDITING',
    },
    settings: '/order-settings/all',
    benefits: [
      {
        title: 'Let customers self-serve',
        description:
          'Give shoppers a way to make supported changes after placing an order.',
      },
      {
        title: 'Set the right boundaries',
        description:
          'Review the editing experience and the changes available to your customers.',
      },
      {
        title: 'Keep orders moving',
        description:
          'Bring order changes back into your existing order workflow.',
      },
    ],
    steps: [
      {
        title: 'Review the editing experience',
        description:
          'Open Order Editing settings to review the installation guide and supported customer changes.',
      },
      {
        title: 'Add Order Editing in Shopify',
        description:
          'Follow the guide to place the Order Editing block on the appropriate checkout and customer surfaces.',
      },
      {
        title: 'Test a customer order',
        description:
          'Place a test order and confirm the editing experience works with your fulfillment process.',
      },
    ],
  },
  {
    id: 'returns-exchanges',
    name: 'Returns & Exchanges',
    category: 'TURN THE RETURN INTO THE NEXT ORDER',
    icon: RefreshCw,
    headline: 'A smoother way\nto make it right.',
    description:
      'Give customers a clear path to a return, exchange, or store credit. Keep every request and next step in one place.',
    activeHeadline: 'Your returns workspace, ready when you are.',
    activeDescription:
      'Pick up customer requests, review the details, and move each return toward the right resolution.',
    setup: {
      label: 'Set up returns',
      href: '/settings/resolution-portal/returns/policy',
    },
    launch: { label: 'Open returns workspace', href: '/returns' },
    settings: '/settings/resolution-portal/returns/policy',
    benefits: [
      {
        title: 'Your policy, clearly applied',
        description:
          'Define return windows, eligibility, and the options customers can choose.',
      },
      {
        title: 'Give the sale another chance',
        description:
          'Offer exchanges and store credit alongside eligible refund options.',
      },
      {
        title: 'One connected workflow',
        description:
          'Review requests, labels, and resolutions from the same workspace.',
      },
    ],
    steps: [
      {
        title: 'Define your return policy',
        description:
          'Choose the return window, item eligibility, and available resolutions for your store.',
      },
      {
        title: 'Customize the Resolve Center',
        description:
          'Review your customer portal, notifications, and return label settings.',
      },
      {
        title: 'Walk through a test return',
        description:
          'Check a customer request from submission through review and resolution.',
      },
    ],
  },
  {
    id: 'delivery-guarantee',
    name: 'Delivery Guarantee',
    category: 'CONFIDENCE FROM CHECKOUT TO DOORSTEP',
    icon: ShieldCheck,
    headline: 'Deliver confidence.\nEven when things go wrong.',
    description:
      'Offer a Delivery Guarantee at checkout and give your team one place to resolve lost, damaged, or stolen package issues.',
    activeHeadline: 'Your Delivery Guarantee is ready to work.',
    activeDescription:
      'Manage delivery issues, review performance, and fine-tune how your guarantee appears to customers.',
    setup: { label: 'Set up Delivery Guarantee', href: '/settings/protection' },
    launch: { label: 'Open issue workspace', href: '/claims' },
    settings: '/settings/protection',
    benefits: [
      {
        title: 'Confidence at checkout',
        description:
          'Give customers a clear Delivery Guarantee option before they place an order.',
      },
      {
        title: 'A clear path to resolution',
        description:
          'Manage delivery issues and choose the right resolution for each request.',
      },
      {
        title: 'Built around your store',
        description:
          'Control your fee, customer experience, and guarantee settings.',
      },
    ],
    steps: [
      {
        title: 'Configure your guarantee',
        description:
          'Set your Delivery Guarantee fee and review how the offer works for your store.',
      },
      {
        title: 'Connect the storefront experience',
        description:
          'Enable the SHIPAID widget in your theme and confirm the guarantee is visible on your cart surfaces.',
      },
      {
        title: 'Review billing and test checkout',
        description:
          'Confirm billing is ready, then place a test order and review the customer resolution experience.',
      },
    ],
  },
  {
    id: 'impact',
    name: 'Impact',
    category: 'SMALL ACTIONS. MEANINGFUL POSSIBILITIES.',
    icon: Sprout,
    headline: 'Make every order\nmean a little more.',
    description:
      'Bring tree planting and charitable giving into your customer experience through SHIPAID Impact add-ons.',
    activeHeadline: 'Keep your impact growing.',
    activeDescription:
      'Review your enabled add-ons and shape how your store shares its impact with customers.',
    setup: { label: 'Explore impact settings', href: '/settings/impact' },
    launch: { label: 'Manage impact', href: '/settings/impact' },
    settings: '/settings/impact',
    benefits: [
      {
        title: 'Tree planting',
        description:
          'Explore the tree planting add-on for your guaranteed orders.',
      },
      {
        title: 'Charitable giving',
        description:
          'Connect customers with the giving experience available through Free Gifts.',
      },
      {
        title: 'An experience that fits',
        description:
          'Review add-on settings and customer messaging for your brand.',
      },
    ],
    steps: [
      {
        title: 'Choose an impact add-on',
        description:
          'Explore Tree Planting and Free Gifts in your Impact settings.',
      },
      {
        title: 'Review the details',
        description:
          'Check the terms and any applicable costs for the add-on before enabling it.',
      },
      {
        title: 'Preview the customer experience',
        description:
          'Confirm the messaging and placement fit naturally into your customer journey.',
      },
    ],
  },
  {
    id: 'shipping-rates',
    name: 'Shipping Rates',
    category: 'MORE BUYING POWER. MORE ROOM TO SAVE.',
    icon: Truck,
    headline: 'Enterprise rates.\nPowered by collective volume.',
    description:
      'Join 5,000+ merchants using our collective volume to access up to 90% off carrier rates while keeping your own carrier accounts.',
    activeHeadline: 'Your shipping portal is one click away.',
    activeDescription:
      'Head to your shipping workspace to compare available services, create labels, and keep shipments moving.',
    setup: {
      label: 'Apply for shipping rates',
      href: 'https://apply.shipaid.com',
    },
    launch: { label: 'Open shipping portal', href: 'https://ship.shipaid.com' },
    secondary: {
      label: 'Already approved? Open shipping portal',
      href: 'https://ship.shipaid.com',
    },
    benefits: [
      {
        title: 'Collective buying power',
        description:
          'Our combined shipping volume opens access to enterprise-level carrier rates. Put that buying power behind your next shipment.',
      },
      {
        title: 'Your carrier accounts',
        description:
          'Keep your own carrier accounts, control, and visibility while accessing SHIPAID rates.',
      },
      {
        title: 'Tailored savings analysis',
        description:
          'Potential savings depend on your volume, lanes, and shipping mix. Apply for a review to see what your business could save.',
      },
    ],
    steps: [
      {
        title: 'Submit your rate application',
        description:
          'Share your shipping profile at apply.shipaid.com. Choose small parcel, FTL/LTL freight, or ocean freight for your savings review.',
      },
      {
        title: 'Review your potential savings',
        description:
          'Get a tailored rate review based on your volume, lanes, and shipping mix, then complete the approval and account setup steps.',
      },
      {
        title: 'Open the shipping portal',
        description:
          'Once approved and set up, sign in at ship.shipaid.com to access your available services.',
      },
    ],
  },
  {
    id: 'fulfillment',
    name: 'Fulfillment',
    category: 'FROM YOUR SHELVES TO THEIR DOORSTEP',
    icon: Warehouse,
    headline: 'Less time packing.\nMore room to grow.',
    description:
      'Explore fulfillment with SHIPAID and bring your inventory, warehouse operations, and order fulfillment into a dedicated workspace.',
    activeHeadline: 'Your fulfillment workspace is one click away.',
    activeDescription:
      'Open your fulfillment portal to stay connected to inventory, orders, and warehouse operations.',
    setup: {
      label: 'Explore fulfillment',
      href: 'https://calendly.com/shipaid/fulfillment-audit',
    },
    launch: {
      label: 'Open fulfillment portal',
      href: 'https://fulfill.shipaid.com',
    },
    secondary: {
      label: 'Already a customer? Open fulfillment portal',
      href: 'https://fulfill.shipaid.com',
    },
    benefits: [
      {
        title: 'A home for your inventory',
        description:
          'Bring inventory and warehouse coordination into a dedicated portal.',
      },
      {
        title: 'Connected order operations',
        description:
          'Keep a clear view of the orders moving through fulfillment.',
      },
      {
        title: 'Support for your next stage',
        description:
          'Explore the setup that fits your products and fulfillment needs.',
      },
    ],
    steps: [
      {
        title: 'Schedule a fulfillment audit',
        description:
          'Book time with the SHIPAID team to review your fulfillment needs.',
      },
      {
        title: 'Plan your onboarding',
        description:
          'Confirm your products, warehouse needs, and account setup with the fulfillment team.',
      },
      {
        title: 'Start fulfilling orders',
        description:
          'Once your setup is complete, use the portal to manage your fulfillment operations.',
      },
    ],
  },
];

export const findFeature = (id: string) =>
  featureCatalog.find((feature) => feature.id === id);
